import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applyDirection, compareValues } from '../../../utils/sorting-utils';

export type SortDirection = 'asc' | 'desc';

/**
 * Returns the value a row should be sorted by for a given column. The value may
 * be a string or number (or null/undefined), and is compared with the shared
 * `compareValues` helper so behaviour matches the rest of the app.
 */
export type SortAccessor<T> = (row: T) => string | number | null | undefined;

export interface SortableHeadProps {
  active: boolean;
  direction: SortDirection;
  onSort: () => void;
}

interface UseSortableTableOptions<T> {
  /** The full (already filtered) data set to sort and paginate. */
  data: T[];
  /** Map of column key -> accessor used when that column is the active sort. */
  accessors: Record<string, SortAccessor<T>>;
  /** Column key sorted by default. */
  initialSortField: string;
  /** Default sort direction for the initial field. Defaults to 'asc'. */
  initialSortDirection?: SortDirection;
  /** Default rows shown per page. Defaults to 10. */
  initialRowsPerPage?: number;
}

interface UseSortableTableResult<T> {
  /** The full data set after sorting (no pagination applied). */
  sortedRows: T[];
  /** The rows for the current page. */
  pageRows: T[];
  sortField: string;
  sortDirection: SortDirection;
  page: number;
  rowsPerPage: number;
  /** True when the current page is the last page of data. */
  isLastPage: boolean;
  /** Props to spread onto a `SortableHeadCell` for the given column key. */
  getSortProps: (field: string) => SortableHeadProps;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Client-side sorting + pagination for the report tables.
 *
 * Sorting and pagination for reports are handled client-side: report payloads
 * are already fully loaded into the browser, so this avoids a backend round-trip
 * and keeps each report's custom presentation (totals rows, severity colouring,
 * chart toggles) intact.
 */
export function useSortableTable<T>({
  data,
  accessors,
  initialSortField,
  initialSortDirection = 'asc',
  initialRowsPerPage = 10,
}: UseSortableTableOptions<T>): UseSortableTableResult<T> {
  const [sortField, setSortField] = useState(initialSortField);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSortDirection);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Keep the latest accessors without forcing callers to memoize them.
  const accessorsRef = useRef(accessors);
  accessorsRef.current = accessors;

  // A new data set (e.g. after re-running a report) returns to the first page.
  useEffect(() => {
    setPage(0);
  }, [data]);

  const handleSort = useCallback((field: string) => {
    setSortField(prevField => {
      if (prevField === field) {
        setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortDirection('asc');
      }
      return field;
    });
    setPage(0);
  }, []);

  const sortedRows = useMemo(() => {
    const accessor = accessorsRef.current[sortField];
    if (!accessor) return data;
    return [...data].sort((a, b) =>
      applyDirection(compareValues(accessor(a), accessor(b)), sortDirection)
    );
  }, [data, sortField, sortDirection]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);

  const pageRows = useMemo(() => {
    const start = safePage * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, safePage, rowsPerPage]);

  const getSortProps = useCallback(
    (field: string): SortableHeadProps => ({
      active: sortField === field,
      direction: sortField === field ? sortDirection : 'asc',
      onSort: () => handleSort(field),
    }),
    [sortField, sortDirection, handleSort]
  );

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  return {
    sortedRows,
    pageRows,
    sortField,
    sortDirection,
    page: safePage,
    rowsPerPage,
    isLastPage: safePage >= pageCount - 1,
    getSortProps,
    handleChangePage,
    handleChangeRowsPerPage,
  };
}
